/**
 * Logging utility that only displays messages in
 * development and staging environments.
 */

/**
 * Checks if the current environment is development or staging
 */
const isDevOrStaging = (): boolean => {
  // APP_ENVIRONMENT takes priority if defined
  if (process.env.APP_ENVIRONMENT) {
    return (
      process.env.APP_ENVIRONMENT === "development" ||
      process.env.APP_ENVIRONMENT === "staging"
    );
  }

  // Fallback to NODE_ENV if APP_ENVIRONMENT is not defined
  return process.env.NODE_ENV === "development";
};

/**
 * Logging function that only displays messages in
 * development and staging environments.
 *
 * Usage identical to console.log
 */
export const devLog = (...args: any[]): void => {
  if (isDevOrStaging()) {
    console.log("[DEV]", ...args);
  }
};

/**
 * Error logging variant
 */
export const devError = (...args: any[]): void => {
  if (isDevOrStaging()) {
    console.error("[DEV ERROR]", ...args);
  }
};

/**
 * Warning logging variant
 */
export const devWarn = (...args: any[]): void => {
  if (isDevOrStaging()) {
    console.warn("[DEV WARN]", ...args);
  }
};

/**
 * Info logging variant
 */
export const devInfo = (...args: any[]): void => {
  if (isDevOrStaging()) {
    console.info("[DEV INFO]", ...args);
  }
};

/**
 * Function to log execution time of a function
 * @param label - Name of the operation to measure
 * @param fn - Function to execute
 * @returns The result of the executed function
 *
 * @example
 * // Example with async function
 * const data = await devTime('fetch users', async () => {
 *   return await fetchUsersFromAPI();
 * });
 *
 * // Example with synchronous function
 * const result = devTime('calculate total', () => {
 *   return items.reduce((sum, item) => sum + item.price, 0);
 * });
 */
export const devTime = async <T>(
  label: string,
  fn: () => Promise<T> | T,
): Promise<T> => {
  if (!isDevOrStaging()) {
    return fn() as Promise<T> | T;
  }

  console.time(`[DEV TIME] ${label}`);
  try {
    const result = await fn();
    console.timeEnd(`[DEV TIME] ${label}`);
    return result;
  } catch (error) {
    console.timeEnd(`[DEV TIME] ${label}`);
    throw error;
  }
};

const devLogger = {
  log: devLog,
  error: devError,
  warn: devWarn,
  info: devInfo,
  time: devTime,
  isDevOrStaging,
};

export default devLogger;
